import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "../../../hooks";
import { Modal } from "../Modal";
import { Loader } from "../../Loader/Loader";
import { ErrorSnackbar } from "../../ErrorSnackbar/ErrorSnackbar";
import { INotify, NotifyType } from "../../../model/user-info.interface";
import rewardIcon from '../ModalBonus/icons/bonus.png';
import { Button } from "../../Buttons/Button/Button";
import { markNotifyAsRead } from "../../../redux/notifications/mark-notify-as-read.handler";
import { NoReferalsBehaviorNotify } from "./BehaviorNotifies/NoReferalsBehaviorNotify";
import { MarkdownWithPlaceholders } from "./MarkdownWithPlaceholders";
import { FormattedMessage } from "./FormattedMessage";
import { customizeNotify, removeNotifyById } from "../../../redux/notifications";
import { CustomNotifyRouter, CustomNotifyType } from "./Custom/CustomNotifyRouter";

export enum BehaviorType {
    NoReferals = 1,
}

export const ModalNotify = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [notify, setNotify] = useState<INotify | null>(null);
    const { items: notifications, loading, error } = useSelector(store => store.notifications);

    const dispatch = useDispatch();    

    const notificationItems = useMemo(() => notifications, [notifications]);

    useEffect(() => {
        if (!notificationItems)
            return;

        if (notificationItems.length) {
            notificationItems.forEach(notify => {
                if (notify.type === NotifyType.ChallengeReward)
                    dispatch(customizeNotify({ id: notify.id, type: CustomNotifyType.ChallengeReward }));
            });

            setNotify(notificationItems[0]);
            setIsOpen(true);
        }
    }, [notificationItems, dispatch]);

    const handler = useCallback(async () => {
        if (!notificationItems.length || !notify)
            return;

        if (notify.type === NotifyType.Custom) {
            dispatch(removeNotifyById(notify.id));
            return;
        }

        dispatch(markNotifyAsRead(notify));
    }, [notificationItems.length, notify, dispatch]);

    const icon = useMemo(() => {
        if (!notify)
            return '';

        switch (notify.type) {
            case NotifyType.DrawResult:
            case NotifyType.FragmentReward: return rewardIcon;
            default: return '';
        }
    }, [notify]);

    const title = useMemo(() => {
        if (!notify)
            return '';

        switch (notify.type) {
            case NotifyType.Bahavior: return 'Подсказка';
            default: return notify.title;
        }
    }, [notify]);

    const behaviorMessage = useMemo(() => {
        if (!notify)
            return null;

        switch (+notify.message) {
            case BehaviorType.NoReferals: 
                return <NoReferalsBehaviorNotify closeHandler={handler} />;
            default: return null;
        }
    }, [notify, handler]);

    const message = useMemo(() => {
        if (!notify)
            return null;

        switch (notify.type) {
            case NotifyType.Bahavior:
                return behaviorMessage;
            
            default:
                if (notify.isMarkdown) {
                    return <MarkdownWithPlaceholders message={notify.message} />;
                } else {
                    return <FormattedMessage message={notify.message} />;
                }
        }
    }, [notify, behaviorMessage]);

    const closeButton = useMemo(() => 
        <Button caption='Закрыть' type='orange' handler={handler} />,
        [handler]
    );

    const button = useMemo(() => {
        if (!notify)
            return closeButton;
        return closeButton;
    }, [notify, closeButton]);

    if (!notify)
        return null;

    const notifyContent = notify.customType
        ? <CustomNotifyRouter {...notify} closeHandler={handler} />
        : notify.type === NotifyType.Bahavior
            ? behaviorMessage
            : (
                <Modal
                    name='ModalNotify'
                    title={title}
                    message={message}
                    icon={icon}
                    button={button}
                />
            );

    return (
        <>
            {isOpen && notificationItems && notificationItems.length > 0 && notifyContent}

            {loading && <Loader />}
            {error.length > 0 && <ErrorSnackbar errorMessage={error} />}
        </>
    );
}